import { isExpired } from "react-jwt";
import { AppSettings, getAppSettings } from "../../hooks/settings/settings";
import { ApiService } from "../api-service";
import { UserCache } from "../caches";
import { AuthenticationService, RegisterUser } from "@nt-portal/server-sdk";
import { SocketService } from "../socket";

//In minutes
export const timeoutCheckMinutes: number = 3
//In seconds
export const timeoutCheckSec: number = timeoutCheckMinutes * 60;

let lastTokenRefresh: number = 0;
export const setTokenRefresh = () => {
    lastTokenRefresh = Date.now();
}
export const shouldRefreshToken = () => {
    // Check if the token should be refreshed based on the last refresh time 1 minute.
    return (Date.now() - lastTokenRefresh) / 1000 > (1 * 60);
}

export interface DataStoredInToken {
    exp: number;
    oid: string;
    email?: string;
    preferred_username?: string
    name: string;
    iss?: string;
}

export function parseJwt(token: string) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

export class JwtAuthService {


    private _settings: AppSettings;


    protected get token() {
        return "";
    }

    protected get settings() {
        return this._settings;
    }

    private _apiService: ApiService;

    protected get apiService() {
        return this._apiService;
    }

    constructor() {
        this._settings = getAppSettings();
        this._apiService = new ApiService();



    }

    private get loginService() {
        if (!this.settings.fabrixServer) {
            throw new Error("No Fabrix Server defined.")
        }


        return new AuthenticationService({
            url: this.settings.fabrixServer,
            apiService: this.apiService
        });


    }

    public onSessionChanged?: (sessionId: string) => void;

    public async logout() {
        let token = UserCache.idToken;
        if (token) {
            if (JwtAuthService.isAuthenticated()) {
                await this.loginService.logout(token);
            }
            UserCache.clear();
        }
    }

    public async forgotPassword(userName: string, email: string) {
        let response = await this.loginService.forgotPassword(userName, email, this.token);
        return response;
    }

    public async registerUser(user: RegisterUser) {
        let response = await this.loginService.register(user, this.token);
        return response;
    }

    public async importUsers(file: File) {
        let service = this.loginService;
        let response = await service.importExcel(file, this.token);
        return response;
    }

    public async authenticate(userName: string, password: string) {

        let response = await this.loginService.authenticate(userName, password, '');
        if (response) {
            UserCache.idToken = response.access_token;
            this.doSessionCheck(response.access_token);
        } else {
            UserCache.clear();
        }

        return response;

    }

    public async keyLogin(key: string) {

        let response = await this.loginService.keyLogin(key);
        if (response) {
            UserCache.idToken = response.access_token;
            this.doSessionCheck(response.access_token);
        } else {
            UserCache.clear();
        }

        return response;
    }

    private doSessionCheck(token: string) {
        // let sessionId = UserCache.sessionId;
        // let tokenData = parseJwt(token);
        // if (tokenData.sid !== sessionId) {
        //     UserCache.sessionId = tokenData.sid;
        //     if (this.onSessionChanged) {
        //         this.onSessionChanged(tokenData.sid);
        //     }
        //     //Now we need to reset the sockets with the new token
        //     SocketService.reconnect(token);
        // }
    }


    public async silentLogin(): Promise<string> {

        let token = UserCache.idToken

        let response = await this.loginService.refreshToken(token);
        if (response) {
            UserCache.idToken = response.access_token;
            this.doSessionCheck(response.access_token);
            return response.access_token;
        } else {
            UserCache.clear();
        }
        return "";
    }

    public static isAuthenticated(): boolean {

        let token = UserCache.idToken;
        let valid = !isExpired(token);
        return valid;

    }

    public static decodeToken(): any {
        let token = UserCache.idToken;
        let valid = !isExpired(token);
        if (valid)
            return parseJwt(token);
        return null;
    }

    // public static currentEnvironment(): string {
    //     return UserCache.environment;
    // }



    public async getByResetKey(resetKey: string) {
        return await this.loginService.getByResetKey(resetKey, this.token);
    }

    public static tokenExpiry(): Date | null {
        const token = UserCache.idToken;
        if (!token) return null;
        try {
            const payload = parseJwt(token) as DataStoredInToken;
            if (!payload || typeof payload.exp !== "number") return null;
            let date = new Date(payload.exp * 1000);
            return date;
        } catch {
            return null;
        }
    }

    public static msUntilExpiry(): number | null {
        const expiry = JwtAuthService.tokenExpiry();
        if (!expiry) return null;
        return expiry.getTime() - Date.now();
    }

    public static almostExpired(thresholdSeconds: number = 60): boolean {
        const remainingMs = JwtAuthService.msUntilExpiry();

        if (remainingMs === null) return true;
        return remainingMs <= thresholdSeconds * 1000;
    }


}


